import { camelCase } from 'lodash';
import { WcgAdvancedFilterOptions } from '@wcg/frontend-framework';

export function convertKeysToCamelCase<T>(obj: T): T | T[] {
  if (Array.isArray(obj)) {
    return obj.map((v: T) => convertKeysToCamelCase(v)) as T[];
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: convertKeysToCamelCase(obj[key as keyof T]),
      }),
      {},
    ) as T;
  }
  return obj;
}

export function convertSnakeToCamelCase(str: string): string {
  return camelCase(str);
}

export function getNestedValue<T, R>(obj: T, path: string): R | string {
  return path.split('.').reduce((acc: unknown, key: string) => {
    if (typeof acc === 'object' && acc !== null && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return '';
  }, obj) as R | string;
}

export function convertKeysToSnakeCase<T>(obj: T): T extends null | undefined
  ? T
  : T extends Array<infer U>
    ? Array<ReturnType<typeof convertKeysToSnakeCase<U>>>
    : T extends object
      ? {
          [K in keyof T & string as CamelToSnake<K>]: ReturnType<
            typeof convertKeysToSnakeCase<T[K]>
          >;
        }
      : T {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj as any;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnakeCase) as any;
  }

  // More efficient: build object directly instead of spreading
  const result: any = {};
  for (const key of Object.keys(obj)) {
    const snakeKey = camelToSnakeCase(key);
    result[snakeKey] = convertKeysToSnakeCase((obj as any)[key]);
  }
  return result;
}

type CamelToSnake<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnake<U>}`
  : S;

export function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function hasActiveFilters(
  filters: Record<string, WcgAdvancedFilterOptions>,
): boolean {
  if (!filters) return false;
  return (
    Object.values(filters)
      .map((f: WcgAdvancedFilterOptions) => f.value)
      .some((v) => (Array.isArray(v) ? v.length > 0 : !!v)) ||
    Object.values(filters).some(
      (f: WcgAdvancedFilterOptions) =>
        typeof f.formatted === 'string' && f.formatted !== '-',
    )
  );
}
